import { Suspense, lazy, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { V2File } from '../v2Upload.d';
import { GcodeExtractedData, GcodeTool } from '@/workers/gcodeParser';
import FileNameInput from './FileNameInput';
import ControllerLogo from './ControllerLogo';
import { FIELD_LABEL } from './fileCardStyles';

// The same interactive 3D toolpath the model-detail page uses. Lazy so its
// three.js bundle only loads when a card is actually opened.
const ModelViewer = lazy(() => import('@/components/ModelViewer'));

/** Short unit for the numeric fields — the parser's `units` string carries the
 * G-code word (e.g. "Inches (G20)"), which is too long for a value cell. Null
 * when the file never said, so no unit is shown rather than a guessed one. */
function unitOf(x: GcodeExtractedData): string | null {
  if (!x.units) return null;
  return /inch|imperial|g20/i.test(x.units) ? 'in' : 'mm';
}

const withUnit = (value: string, u: string | null) =>
  u ? `${value} ${u}` : value;

const round = (n: number) => Math.round(n * 100) / 100;
const yn = (b: boolean) => (b ? 'Yes' : 'No');

type Field = { label: string; value: string };
const isField = (f: Field | false | '' | null | undefined): f is Field =>
  Boolean(f);

/** A text cell, dropped when the string is empty/unset. */
function textField(
  value: string | null | undefined,
  label: string,
  format: (v: string) => string = (v) => v
): Field | null {
  return value ? { label, value: format(value) } : null;
}

/** A cell whose value is present when `value` is non-null — 0 still shows, so
 * a measured zero isn't silently dropped. `format` sees the narrowed value. */
function numField<T>(
  value: T | null | undefined,
  label: string,
  format: (v: T) => string
): Field | null {
  return value != null ? { label, value: format(value) } : null;
}

/** Everything the parser read out of the file, as label/value cells. Absent
 * fields are dropped so a file that didn't record one simply doesn't show it. */
export function fields(file: V2File, x: GcodeExtractedData): Field[] {
  const u = unitOf(x);
  const feedUnit = u && `${u}/min`;
  const dim = (d: { width: number; height: number; thickness: number }) =>
    withUnit(
      `${round(d.width)} × ${round(d.height)} × ${round(d.thickness)}`,
      u
    );

  return [
    { label: 'Controller', value: file.controller },
    { label: 'Format', value: `.${file.ext} · ${file.badge}` },
    { label: 'File size', value: file.size },
    textField(x.projectName, 'Program'),
    textField(x.units, 'Units'),
    numField(x.extents, 'Cut area', (e) =>
      withUnit(`${round(e.xMax - e.xMin)} × ${round(e.yMax - e.yMin)}`, u)
    ),
    numField(x.dimensions, 'Job size', dim),
    numField(x.stock, 'Stock', dim),
    numField(x.maxCutDepth, 'Max depth', (v) => withUnit(String(round(v)), u)),
    numField(x.safeZ, 'Safe Z', (v) => withUnit(String(round(v)), u)),
    numField(x.cuttingFeed, 'Cutting feed', (v) =>
      withUnit(v.toLocaleString(), feedUnit)
    ),
    numField(x.plungeFeed, 'Plunge feed', (v) =>
      withUnit(v.toLocaleString(), feedUnit)
    ),
    numField(x.spindleSpeed, 'Spindle', (v) => `${v.toLocaleString()} rpm`),
    numField(x.pathLength, 'Cutting travel', (p) =>
      withUnit(round(p.cutting).toLocaleString(), u)
    ),
    numField(x.pathLength, 'Rapid travel', (p) =>
      withUnit(round(p.rapid).toLocaleString(), u)
    ),
    numField(x.estCuttingTimeMin, 'Est. runtime', (v) => `${v} min`),
    x.toolCount > 0 && { label: 'Tools', value: String(x.toolCount) },
    textField(x.camSoftware, 'Design SW'),
    // The version is only appended when it isn't already part of the name.
    // Plenty of post processors carry their year in the name itself ("Masso
    // Carveco Post Processor 2023"), and the parser reads that year into
    // postVersion as well — joining unconditionally rendered "…2023 2023".
    textField(x.postProcessor, 'Post processor', (v) =>
      x.postVersion && !v.endsWith(x.postVersion) ? `${v} ${x.postVersion}` : v
    ),
    textField(x.workCoordSystem, 'Work coord'),
    textField(x.plane, 'Plane'),
    textField(x.distanceMode, 'Distance mode'),
    textField(x.jobType, 'Job type'),
    textField(x.zZeroPosition, 'Z-zero'),
    textField(x.xyDatumPosition, 'XY datum'),
    { label: 'Arcs', value: yn(x.hasArcs) },
    { label: 'Coolant', value: yn(x.coolant) },
    { label: 'Tool setter', value: x.hasProbing ? 'Probing found' : 'No' },
    { label: 'Auto tool change', value: yn(x.hasATC) },
    textField(x.programEnd, 'Program end'),
  ].filter(isField);
}

/** `format(value)` when value is set, else null — keeps the optional-field
 * branch out of the callers that assemble a line from several such fields. */
function optPart(
  value: number | null | undefined,
  format: (v: number) => string
): string | null {
  return value != null ? format(value) : null;
}

/** The geometry a tool declares — diameter, V-angle, tip, cone — as one muted
 * line under its name. Empty when the file gave only a tool number. */
function toolGeometry(t: GcodeTool): string {
  const u = t.unit ?? '';
  return [
    optPart(t.diameter, (v) => `⌀ ${round(v)} ${u}`.trim()),
    optPart(t.vBitDegree, (v) => `${v}°`),
    optPart(t.tipRadius, (v) => `tip ${round(v)} ${u}`.trim()),
    optPart(t.coneAngle, (v) => `cone ${v}°`),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The T-number chip, or "T?" when the file didn't number the tool. */
function ToolChip({ toolNumber }: { toolNumber: number | null | undefined }) {
  return (
    <span className="w-8 h-8 shrink-0 rounded-full bg-muted text-heading flex items-center justify-center text-[11px] font-semibold">
      {toolNumber != null ? `T${toolNumber}` : 'T?'}
    </span>
  );
}

/** Speed and feed, each shown only when the file recorded it. */
function ToolBadges({ tool, unit }: { tool: GcodeTool; unit: string }) {
  return (
    <div className="shrink-0 flex items-center gap-1.5">
      {tool.spindleSpeed != null && (
        <Badge variant="secondary" className="text-[10px] font-normal">
          {tool.spindleSpeed.toLocaleString()} rpm
        </Badge>
      )}
      {tool.feedRate != null && (
        <Badge variant="secondary" className="text-[10px] font-normal">
          {withUnit(tool.feedRate.toLocaleString(), unit && `${unit}/min`)}
        </Badge>
      )}
    </div>
  );
}

/** One tool as a row: a T-number chip, its type and geometry, and the speed and
 * feed it runs at pushed to the right. */
function ToolRow({ tool }: { tool: GcodeTool }) {
  const u = tool.unit ?? '';
  const geometry = toolGeometry(tool);

  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <ToolChip toolNumber={tool.toolNumber} />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold truncate">
          {tool.type ?? 'Tool'}
        </div>
        {geometry && (
          <div className="text-[11px] font-tertiary text-muted-foreground truncate mt-0.5">
            {geometry}
          </div>
        )}
      </div>
      <ToolBadges tool={tool} unit={u} />
    </div>
  );
}

/** The 3D toolpath, dormant until clicked. OrbitControls swallows the wheel to
 * zoom, which traps the modal's scroll the moment the pointer is over it — so
 * the canvas ignores pointer events until you click in, and hands them back
 * when the pointer leaves. Scrolling the modal just works; rotating is one
 * click away. */
function ToolpathViewer({
  url,
  badge,
  controller,
}: {
  url: string;
  badge: string;
  controller: string;
}) {
  const [active, setActive] = useState(false);

  return (
    <div
      className="relative h-[380px] rounded-xl border bg-muted overflow-hidden"
      onMouseLeave={() => setActive(false)}
    >
      <Badge
        variant="secondary"
        className="absolute top-2 left-2 z-10 h-5 px-1 gap-1 text-[10px]"
      >
        <ControllerLogo name={controller} className="w-3.5 h-3.5" />
        {badge}
      </Badge>

      <div className={cn('h-full', !active && 'pointer-events-none')}>
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
              Loading 3D preview…
            </div>
          }
        >
          <ModelViewer
            modelURL={url}
            enableGrid
            backgroundColor="transparent"
            modelColor="#136245"
            frameloop="demand"
          />
        </Suspense>
      </div>

      {!active && (
        <button
          type="button"
          onClick={() => setActive(true)}
          className="absolute inset-0 flex items-end justify-center pb-3 bg-transparent"
          aria-label="Activate 3D preview"
        >
          <span className="rounded-full bg-foreground/80 px-3 py-1 text-[10px] font-semibold text-background">
            Click to rotate · scroll to zoom
          </span>
        </button>
      )}
    </div>
  );
}

/** The full-view a G-code card opens to: the toolpath at size, its controller,
 * a rename field, and everything the parser read out of this one file. Read
 * from `file.extracted`, so each card opens to its own numbers, not the
 * wizard's shared aggregate. */
function extractedOf(file: V2File | null): GcodeExtractedData | null {
  return file?.extracted ?? null;
}

/** The parser read-out for one file: the field grid, tool rows and operation
 * chips. Tools/operations sections show only when the file recorded them. */
function ExtractedDetails({
  file,
  x,
}: {
  file: V2File;
  x: GcodeExtractedData;
}) {
  return (
    <>
      <div>
        <div className={cn(FIELD_LABEL, 'mb-2')}>Read from this file</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-border border rounded-xl overflow-hidden">
          {fields(file, x).map((f) => (
            <div key={f.label} className="bg-card p-3">
              <div className={FIELD_LABEL}>{f.label}</div>
              <div
                title={f.value}
                className="text-sm font-medium mt-1 truncate"
              >
                {f.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {x.tools.length > 0 && (
        <div>
          <div className={cn(FIELD_LABEL, 'mb-2')}>
            Tools ({x.tools.length})
          </div>
          <div className="rounded-xl border divide-y overflow-hidden">
            {x.tools.map((t) => (
              <ToolRow key={t.raw} tool={t} />
            ))}
          </div>
        </div>
      )}

      {x.operations.length > 0 && (
        <div>
          <div className={cn(FIELD_LABEL, 'mb-2')}>
            Operations ({x.operations.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {x.operations.map((op, i) => (
              <Badge
                // Operation names aren't unique (two passes can share one) and
                // the list never reorders, so the index is a stable disambiguator.
                // eslint-disable-next-line react/no-array-index-key
                key={`${op}-${i}`}
                variant="secondary"
                className="text-[10px] font-normal"
              >
                {op}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default function GcodeDetailModal({
  file,
  onClose,
}: {
  file: V2File | null;
  onClose: () => void;
}) {
  const open = Boolean(file);
  const x = extractedOf(file);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* p-0 + overflow-hidden so the ScrollArea owns the scroll and its bar
          sits inside the rounded corners — the native scrollbar was cutting
          across the close button and the corner. */}
      <DialogContent className="max-w-2xl gap-0 p-0 overflow-hidden">
        {file && (
          <ScrollArea className="max-h-[92vh]">
            {/* Radix requires a DialogTitle on every DialogContent, and without
                one it logs an error and the dialog opens unnamed for a screen
                reader. It is `sr-only` rather than rendered: the modal's visible
                heading is the 3D toolpath, and the filename below it is an
                editable input, so there is nothing on screen to promote — but
                the file's name is exactly what the dialog should announce. */}
            <DialogTitle className="sr-only">{file.name}</DialogTitle>
            <DialogDescription className="sr-only">
              Toolpath preview and the details read out of this G-code file.
            </DialogDescription>
            <div className="grid gap-4 p-6">
              <ToolpathViewer
                url={file.preview}
                badge={file.badge}
                controller={file.controller}
              />

              <div>
                <div className={cn(FIELD_LABEL, 'mb-1.5')}>Filename</div>
                <FileNameInput file={file} />
              </div>

              {x ? (
                <ExtractedDetails file={file} x={x} />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Still reading this file…
                </p>
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
